import { memo, useMemo } from 'react';
import {
  CONFIDENCE_LABELS,
  CONFIDENCE_DESCRIPTIONS,
  summarizeConfidence,
  isConfidenceLevel,
  type ConfidenceLevel,
  type FactKey,
} from '@genealogy-app/shared';
import type { PersonDetail } from '@/api/persons.api';

// ─── Sicherheitsgrad einer Angabe ────────────────────────────────────────────
//
// Ohne diese Kennzeichnung ist ein belegtes Geburtsdatum optisch nicht von
// einem geratenen „ca. 1850" zu unterscheiden. Die Farbe zeigt auf einen Blick,
// wie belastbar eine Angabe ist; Widersprüche stechen bewusst heraus.

const STYLES: Record<ConfidenceLevel, string> = {
  primary: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  secondary: 'bg-blue-50 text-blue-700 ring-blue-200',
  assumed: 'bg-amber-50 text-amber-700 ring-amber-200',
  conflicting: 'bg-red-50 text-red-700 ring-red-200',
};

/** Kurzzeichen, damit das Abzeichen auch im schmalen Panel Platz hat. */
const SHORT: Record<ConfidenceLevel, string> = {
  primary: 'gesichert',
  secondary: 'wahrsch.',
  assumed: 'vermutet',
  conflicting: 'widersprüchl.',
};

interface FactConfidenceBadgeProps {
  level: ConfidenceLevel;
  /** Anzahl der Belege hinter dieser Einstufung. */
  count?: number;
}

export const FactConfidenceBadge = memo(function FactConfidenceBadge({
  level,
  count,
}: FactConfidenceBadgeProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${STYLES[level]}`}
      title={`${CONFIDENCE_LABELS[level]} – ${CONFIDENCE_DESCRIPTIONS[level]}${
        count && count > 1 ? ` (${count} Belege)` : ''
      }`}
    >
      {SHORT[level]}
      {count && count > 1 ? ` ·${count}` : ''}
    </span>
  );
});

/**
 * Fasst die Belege einer Person je Angabe zusammen: welcher Sicherheitsgrad
 * gilt und wie viele Belege stehen dahinter.
 */
export function useFactConfidence(person: PersonDetail) {
  return useMemo(() => {
    const byFact = new Map<string, { level: ConfidenceLevel; count: number }>();
    const grouped = new Map<string, ConfidenceLevel[]>();

    for (const c of person.factCitations ?? []) {
      if (!isConfidenceLevel(c.confidence)) continue;
      const list = grouped.get(c.fact) ?? [];
      list.push(c.confidence);
      grouped.set(c.fact, list);
    }

    for (const [fact, levels] of grouped) {
      const level = summarizeConfidence(levels);
      if (level) byFact.set(fact, { level, count: levels.length });
    }
    return byFact;
  }, [person.factCitations]);
}

/** Bequemer Zugriff im JSX: Abzeichen für eine Angabe oder nichts. */
export function FactBadge({
  fact,
  confidenceByFact,
}: {
  fact: FactKey;
  confidenceByFact: Map<string, { level: ConfidenceLevel; count: number }>;
}) {
  const entry = confidenceByFact.get(fact);
  if (!entry) return null;
  return <FactConfidenceBadge level={entry.level} count={entry.count} />;
}
